import PropTypes from 'prop-types';

// ---------------------------------------------------------

import styles from './styles.module.scss';

// ---------------------------------------------------------

/**
 * Visible "Where We Serve" section for service/program pages. It surfaces the
 * coverage area, service type, and audience as on-page content so the values
 * emitted in the Service structured data (areaServed / serviceType / audience)
 * are verifiable from the rendered page per Google's content-parity guidance.
 *
 * Coverage is grouped by state (each with its primary metros/regions) so the
 * statewide footprint reads as intentional rather than a redundant flat list.
 */
const WhereWeServe = (props) => {
  const { regions = [], audience, introPhrase, providerName } = props;

  if (regions.length === 0) return null;

  const intro = introPhrase
    ? `${providerName} provides ${introPhrase} across the communities we call home:`
    : `${providerName} serves the following areas:`;

  return (
    <section className={styles.wrapper} aria-labelledby="where-we-serve-heading">
      <div className={styles.inner}>
        <h2 id="where-we-serve-heading">Where We Serve</h2>
        <p>{intro}</p>
        <ul className={styles.areas}>
          {regions.map(({ state, areas = [] }) => (
            <li key={state}>
              <span className={styles.state}>{state}</span>
              {areas.length > 0 && <span className={styles.detail}>{areas.join(', ')}</span>}
            </li>
          ))}
        </ul>
        {audience && (
          <p className={styles.audience}>
            <strong>Who it&apos;s for:</strong> {audience}
          </p>
        )}
      </div>
    </section>
  );
};

WhereWeServe.propTypes = {
  /**
   * Who the service is for; mirrors the Service schema `audience`.
   */
  audience: PropTypes.string,

  /**
   * Object of "provides ___" in the intro sentence, authored per service config.
   * Omitting it falls back to a generic "serves the following areas" sentence.
   */
  introPhrase: PropTypes.string,

  /**
   * Provider name shown in the intro sentence.
   */
  providerName: PropTypes.string.isRequired,

  /**
   * Coverage areas grouped by state; together these mirror the Service schema
   * `areaServed` values so the footprint is verifiable on the page.
   */
  regions: PropTypes.arrayOf(
    PropTypes.shape({
      state: PropTypes.string.isRequired,
      areas: PropTypes.arrayOf(PropTypes.string)
    })
  )
};

export default WhereWeServe;
